import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchBatch, fetchBatchPreview } from '../features/approvals/api.js';
import { ApprovalView } from '../features/approvals/approval-view.js';
import type { ApprovalBatch, PublicApprovalBatch } from '../features/approvals/types.js';
import { monthFromLabel } from '../features/client-calendar/client-calendar-view.js';
import { InternalCalendar } from '../features/client-calendar/internal-calendar.js';

function PreviewPage() {
  const { batchId } = Route.useParams();
  const q = useQuery<PublicApprovalBatch>({
    queryKey: ['approval-preview', batchId],
    queryFn: () => fetchBatchPreview(batchId),
    retry: false,
  });
  const meta = useQuery<ApprovalBatch>({
    queryKey: ['approval-batch', batchId],
    queryFn: () => fetchBatch(batchId),
    retry: false,
  });

  if (q.isLoading) {
    return <p className="text-body text-[var(--fg-2)]">Betöltés…</p>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-lg border border-[var(--st-rejected-bg)] bg-[var(--st-rejected-bg)] p-6 text-[var(--danger)]">
        <p className="font-semibold">Nem sikerült betölteni a csomagot.</p>
        <p className="mt-1 text-body">
          {(q.error as Error | undefined)?.message ?? 'Ismeretlen hiba.'}
        </p>
        <Link
          to="/app/approvals"
          className="mt-3 inline-block text-body text-[var(--brand-700)] underline"
        >
          ← Vissza a jóváhagyásokhoz
        </Link>
      </div>
    );
  }

  return (
    <ApprovalView
      batch={q.data}
      token={null}
      batchId={batchId}
      readOnly
      calendar={
        meta.data ? (
          <InternalCalendar
            cacheKey={batchId}
            hotelId={meta.data.hotelId}
            email={meta.data.clientEmail}
            initial={monthFromLabel(q.data.monthLabel)}
          />
        ) : (
          <p className="text-body text-[var(--fg-2)]">A naptár betöltése…</p>
        )
      }
      topSlot={
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--brand-200)] bg-[var(--ai-bg)] px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[var(--brand-500)]" />
            <span className="text-meta font-semibold text-[var(--ink-700)]">
              Belső előnézet — pontosan ezt látja az ügyfél a linken
            </span>
          </div>
          <Link
            to="/app/approvals"
            className="text-meta font-medium text-[var(--brand-700)] hover:underline"
          >
            ← Vissza
          </Link>
        </div>
      }
    />
  );
}

export const Route = createFileRoute('/app/approvals/$batchId/preview')({
  component: PreviewPage,
});
